// @ts-nocheck
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface MentionSuggestion {
  id: string;
  name: string;
  username: string | null;
  avatar_url: string | null;
  sport_id: number | null;
}

export const useMentionSearch = () => {
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchAthletes = useCallback(async (query: string) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, username, profile_photo_url, sport_id')
        .or(`name.ilike.%${query}%,username.ilike.%${query}%`)
        .eq('is_demo', false)
        .limit(10);

      if (error) throw error;

      setSuggestions(
        (data || []).map(p => ({
          id: p.id,
          name: p.name || 'Unknown',
          username: p.username,
          avatar_url: p.profile_photo_url,
          sport_id: p.sport_id,
        }))
      );
    } catch (err) {
      console.error('Error searching athletes:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return { suggestions, isLoading, searchAthletes, clearSuggestions };
};

// Parse mentions from text content
export const parseMentions = (content: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const matches = content.match(mentionRegex);
  if (!matches) return [];
  return matches.map(m => m.slice(1)); // Remove the @ prefix
};

// Create mention records in database
export const saveMentions = async (
  mentionedUsernames: string[],
  mentionedById: string,
  postId?: string,
  commentId?: string
): Promise<{ profileId: string; username: string }[]> => {
  if (mentionedUsernames.length === 0) return [];

  // Look up profile IDs by username
  const { data: profiles, error: lookupError } = await supabase
    .from('profiles')
    .select('id, username')
    .in('username', mentionedUsernames);

  if (lookupError || !profiles) {
    console.error('Error looking up mentioned users:', lookupError);
    return [];
  }

  // Create mention records
  const mentionRecords = profiles.map(p => ({
    post_id: postId || null,
    comment_id: commentId || null,
    mentioned_profile_id: p.id,
    mentioned_by_id: mentionedById,
  }));

  if (mentionRecords.length > 0) {
    const { error: insertError } = await supabase
      .from('wall_mentions')
      .insert(mentionRecords);

    if (insertError) {
      console.error('Error saving mentions:', insertError);
    }
  }

  return profiles.map(p => ({ profileId: p.id, username: p.username || '' }));
};
